<template>
  <div>
    <div class="relative">
      <span
        v-if="innerMax > 0"
        class="analysis_limit"
      >今月のレビュー分析可能数： {{ innerCount }} / {{ innerMax }}</span>
    </div>
    <vx-card>
      <data-table
        :row-data="rowData"
        :column-defs="columnDefs"
        :default-col-def="defaultColDef"
        :grid-options="gridOptions"
        pagination
      >
        <template #actions>
          <vs-button
            @click="onClickReportsDownload"
          >
            結果エクスポート
          </vs-button>
          <vs-button
            class="ml-4"
            @click="onClickAsinRegister"
          >
            登録
          </vs-button>
        </template>
      </data-table>
    </vx-card>

    <form-modal
      v-model="showAsinForm"
      title="ASIN"
      class="analysis-asin-modal"
      @click:submit="onClickAsinSubmit"
    >
      <template #content>
        <ValidationForm
          ref="asinObserver"
          as="form"
        >
          <asin-field
            v-model="asin"
            rules="required"
            class="w-80 mb-10"
            :options="asinOptions"
          />
        </ValidationForm>
      </template>
    </form-modal>

    <form-modal
      v-model="showFilterForm"
      title="分析情報の登録"
      class="analysis-filter-modal"
      :disabled="filterDisabled || innerCount <= 0"
      @click:submit="onClickFilterSubmit"
    >
      <template #content>
        <review-analysis-filter-form
          v-model:filter="filter"
          v-model:submit-disabled="filterDisabled"
          :product="product"
          :rate="rate"
          :date="date"
          :variation="variation"
        />
      </template>
    </form-modal>

    <form-modal
      v-model="showReportForm"
      title="レビュー分析レポート"
      class="analysis-report-modal"
      :hidden="true"
    >
      <template #content>
        <review-analysis-report-form
          :report="report"
          @on-click-download="onClickDownload"
        />
      </template>
    </form-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import DataTable from '@/components/table/DataTable'
import DataTableCellAnalysis from '@/components/table/DataTableCellAnalysis'
import FormModal from '@/components/modals/FormModal'
import AsinField from '@/components/fields/AsinField'
import ReviewAnalysisFilterForm from '@/components/forms/ReviewAnalysisFilterForm'
import ReviewAnalysisReportForm from '@/components/forms/ReviewAnalysisReportForm'
import analysisStatus from '@/constants/analysisStatus'
import { unique } from '@/utils'

const MAX_ASIN_TITLE_LENGTH = 110

export default {
  name: 'ReviewAnalysis',
  components: {
    DataTable,
    FormModal,
    AsinField,
    ReviewAnalysisFilterForm,
    ReviewAnalysisReportForm
  },
  data () {
    return {
      gridOptions: {},
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMenu: true
      },
      columnDefs: [
        {
          headerName: '登録日',
          field: 'createdAt'
        },
        {
          headerName: 'ASIN',
          field: 'asin'
        },
        {
          headerName: '商品名',
          field: 'title'
        },
        {
          headerName: 'ステータス',
          field: 'status',
          valueFormatter: this.innerStatusText
        },
        {
          headerName: 'アクション',
          field: 'action',
          cellRenderer: 'dataTableCellAnalysis',
          cellRendererParams : {
            action: this.analysisAction.bind(this)
          }
        }
      ],
      showAsinForm: false,
      showFilterForm: false,
      showReportForm: false,
      asin: null,
      reviewAnalysisId: null,
      product: [],
      rate: [],
      date: [],
      variation: [],
      filter: {},
      report: {},
      filterDisabled: false
    }
  },
  computed: {
    ...mapState('analysis', ['limit', 'asins', 'analyses']),
    rowData: {
      get () {
        return JSON.parse(JSON.stringify(this.analyses))
      }
    },
    asinOptions () {
      if (!this.asins) {
        return []
      }
      return this.asins.map((item) => {
        const text = `${item.asin} ${item.title}`
        return {
          value: item.asin,
          text: (text.length > MAX_ASIN_TITLE_LENGTH) ? `${text.substring(0, MAX_ASIN_TITLE_LENGTH)}...` : text
        }
      })
    },
    innerCount: {
      get () {
        return (this.limit) ? this.limit.count : 0
      }
    },
    innerMax: {
      get () {
        return (this.limit) ? this.limit.max : 0
      }
    }
  },
  beforeMount () {
    this.gridOptions = {
      autoSizeStrategy: {
        type: 'fitGridWidth',
        defaultMinWidth: 130,
        columnLimits: [{
          colId: 'createdAt',
          maxWidth: 130
        },{
          colId: 'asin',
          maxWidth: 160
        },{
          colId: 'status',
          maxWidth: 160
        },{
          colId: 'action',
          maxWidth: 160
        }]
      },
      components: {
        dataTableCellAnalysis: DataTableCellAnalysis
      }
    }
  },
  async mounted () {
    try {
      await this.listAnalyses()
      await this.getAnalysisLimit()
      await this.listAsins()
    } catch (e) {
      console.log(e)
    }
  },
  methods: {
    ...mapActions('analysis', ['getAnalysisLimit', 'listAnalyses', 'listAsins', 'addAnalysis', 'getProduct', 'updateFilter', 'getReport', 'downloadCSV', 'downloadReportsCSV']),
    innerStatusText (params) {
      return analysisStatus[params.value]
    },
    async onClickReportsDownload () {
      try {
        await this.downloadReportsCSV()
      } catch (e) {
        console.log(e)
      }
    },
    async onClickAsinRegister () {
      await this.initializeAsin()
      this.showAsinForm = true
    },
    // ASINの登録
    async onClickAsinSubmit () {
      try {
        const result = await this.$refs.asinObserver.validate()
        if (!result.valid) return
        const asin = this.asin.value
        await this.addAnalysis({ asin })
        this.showAsinForm = false
        window.setTimeout(async () => {
          await this.initializeAsin()
        }, 1000)
      } catch (e) {
        console.log(e)
      }
    },
    async analysisAction ({id, status}) {
      this.reviewAnalysisId = id
      if (status === 2) {
        // 分析情報の登録ウィンドウを表示
        await this.initializeFilter(id)
        this.showFilterForm = true
      } else if (status === 4) {
        // レポートウィンドウを表示
        await this.initializeReport(id)
        this.showReportForm = true
      }
    },
    // フィルターの登録
    async onClickFilterSubmit () {
      try {
        await this.updateFilter({ id: this.reviewAnalysisId, filter: JSON.stringify(this.filter) })
        this.showFilterForm = false
        await this.listAnalyses()
        await this.getAnalysisLimit()
      } catch (e) {
        console.log(e)
      }
    },
    async initializeAsin () {
      this.asin = null
      await this.$refs.asinObserver.resetForm()
    },
    async initializeFilter (id) {
      const product = await this.getProduct({ id })
      if (Array.isArray(product)) {
        const filters = product.reduce((dist, p) => {
          dist.rate.push(p.rate)
          dist.date.push(p.date)
          dist.variation.push(p.variation)
          return dist
        }, { date:[], rate:[], variation:[] })
        this.product = product
        this.rate = unique(filters.rate).sort()
        this.date = unique(filters.date).sort()
        this.variation = unique(filters.variation).sort()
      }
    },
    async initializeReport (id) {
      const report = await this.getReport({ id })
      const variation = report.filter.variation.map(v => { return (v === null) ? '不明' : v })
      const start = moment(report.filter.start).format('YYYY/M/D')
      const end = moment(report.filter.end).format('YYYY/M/D')
      this.report = {
        id,
        image: report.samllImage,
        asin: report.asin,
        title: report.title,
        rate: report.filter.rate.join(','),
        variation: (variation.length === 1 && variation[0] === '不明') ? 'なし' : variation.join(','),
        date: `${start} 〜 ${end}`,
        count: report.countFilter,
        all: report.countAll,
        gpt: report.gpt
      }
    },
    async onClickDownload ({id, isAll}) {
      await this.downloadCSV({ id, action: (isAll) ? 'all' : 'filter' })
    }
  }
}

</script>

<style scoped>
.analysis_limit {
  position: absolute;
  top: -1.75rem;
  right: 0;
  font-size: 1.125rem;
}
.analysis-asin-modal :deep(.vs-popup) {
  width: 700px;
}
.analysis-asin-modal :deep(.vs-popup .vs-popup--content) {
  overflow: visible;
}
.analysis-filter-modal :deep(.vs-popup) {
  width: 1000px;
}
.analysis-report-modal :deep(.vs-popup) {
  width: 1000px;
}
</style>
